import { apiClient } from '@/lib/api';
import type {
  CostoDeLaImportacion,
  DatosDeProducto,
  ImportacionAnterior,
  ImportacionDeComercia,
  LugaresDeComercia,
  ProductoDelCatalogo,
  RutasDeComercia,
  TipoDeContenedor,
  UltimaRuta,
  UnidadUbicada,
  ViasDeComercia,
} from '../types/comercia.types';

const BASE = '/api/comercia';

/** Lo que contestan las escrituras: la importación ya actualizada. */
type ConImportacion = { importacion: ImportacionDeComercia | null };

export const comerciaService = {
  actual: () =>
    apiClient.get<{ importacion: ImportacionDeComercia | null; ultima: UltimaRuta | null }>(
      `${BASE}/actual`
    ),
  nueva: () => apiClient.post<ConImportacion>(`${BASE}/nueva`),
  anteriores: () => apiClient.get<ImportacionAnterior[]>(`${BASE}/anteriores`),
  retomar: (id: string) => apiClient.post<ConImportacion>(`${BASE}/anteriores/${id}/retomar`),

  // Productos
  agregarProducto: (datos: DatosDeProducto) =>
    apiClient.post<ConImportacion>(`${BASE}/productos`, datos),
  editarProducto: (id: string, datos: DatosDeProducto) =>
    apiClient.put<ConImportacion>(`${BASE}/productos/${id}`, datos),
  quitarProducto: (id: string) => apiClient.delete<ConImportacion>(`${BASE}/productos/${id}`),
  agregarLote: (lineas: DatosDeProducto[]) =>
    apiClient.post<ConImportacion>(`${BASE}/productos/lote`, { lineas }, { timeout: 120_000 }),
  proveedores: (q: string) => apiClient.get<string[]>(`${BASE}/proveedores`, { params: { q } }),
  catalogo: (q: string) =>
    apiClient.get<ProductoDelCatalogo[]>(`${BASE}/catalogo`, { params: { q } }),

  // Origen y destino
  lugares: () => apiClient.get<LugaresDeComercia>(`${BASE}/lugares`),
  vias: (origenId: string, destinoId: string) =>
    apiClient.get<ViasDeComercia>(`${BASE}/vias`, { params: { origenId, destinoId } }),
  guardarOrigenDestino: (datos: {
    origenId: string;
    destinoId: string;
    puntoLlegadaId: string | null;
    almacen: string | null;
  }) => apiClient.put<ConImportacion>(`${BASE}/origen-destino`, datos),

  // Carga
  tiposDeContenedor: () =>
    apiClient.get<{ estandar: TipoDeContenedor[]; otros: TipoDeContenedor[] }>(
      `${BASE}/contenedores/tipos`
    ),
  crearTipo: (datos: {
    nombre: string;
    largoCm: number;
    anchoCm: number;
    altoCm: number;
    pesoMaxKg: number;
  }) => apiClient.post<TipoDeContenedor>(`${BASE}/contenedores/tipos`, datos),
  agregarContenedor: (tipoId: string) =>
    apiClient.post<ConImportacion>(`${BASE}/contenedores`, { tipoId }),
  cambiarTipo: (id: string, tipoId: string) =>
    apiClient.patch<ConImportacion>(`${BASE}/contenedores/${id}`, { tipoId }),
  quitarContenedor: (id: string) => apiClient.delete<ConImportacion>(`${BASE}/contenedores/${id}`),
  guardarAcomodo: (id: string, lineas: Array<{ productoId: string; unidades: UnidadUbicada[] }>) =>
    apiClient.put<ConImportacion>(
      `${BASE}/contenedores/${id}/acomodo`,
      { lineas },
      {
        timeout: 60_000,
      }
    ),

  // Rutas
  rutas: () => apiClient.get<RutasDeComercia>(`${BASE}/rutas`),
  elegirRuta: (id: string) => apiClient.post<ConImportacion>(`${BASE}/rutas/elegir`, { id }),

  // Costo nacionalizado
  costo: () => apiClient.get<CostoDeLaImportacion>(`${BASE}/costo-nacionalizado`),
  publicar: () =>
    apiClient.post<{ publicadoAt: string; materiales: number }>(
      `${BASE}/costo-nacionalizado/publicar`
    ),
};
